import { authFetch, type AuthOptions } from './auth-fetch';
import { action as parseAgentAction, parseAgentBlock } from './agent-events';
import { apiClient } from './client';
import { streamAgentEvents } from './sse';
import type {
  AgentBlock,
  AgentConversation,
  AgentConversationDetail,
  AgentConversationList,
  AgentEvent,
  AgentPageContext,
  AgentPendingAction,
  AgentStatus,
} from './types';

/**
 * A refusal from `/agent/*`, carrying the backend's own code.
 *
 * Callers branch on `code`, never on `message`: two different 409s (
 * `conversation_busy` and `actions_pending`) mean opposite things, and one of
 * them is safe to retry while the other is not.
 */
export class AgentApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly payload: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'AgentApiError';
  }
}

export interface ResolutionContext {
  page_context: AgentPageContext;
  timezone: string;
}

export interface SendMessageArgs extends ResolutionContext {
  conversationId: string;
  text: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

async function refusal(response: Response): Promise<AgentApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // A proxy timeout or a gateway page: there is no JSON to read.
  }
  const detail = isRecord(body) ? body.detail : null;

  if (isRecord(detail)) {
    const { code, message, ...rest } = detail;
    return new AgentApiError(
      response.status,
      typeof code === 'string' ? code : 'unknown',
      typeof message === 'string' ? message : 'The assistant could not handle that.',
      rest,
    );
  }
  return new AgentApiError(response.status, 'unknown', 'The assistant could not handle that.');
}

async function* post(
  path: string,
  body: unknown,
  options: AuthOptions & { signal?: AbortSignal } = {},
): AsyncGenerator<AgentEvent> {
  const { signal, ...auth } = options;
  const response = await authFetch(
    path,
    { method: 'POST', body: JSON.stringify(body), signal },
    auth,
  );
  if (!response.ok) throw await refusal(response);
  yield* streamAgentEvents(response, signal);
}

export const assistantApi = {
  status: async (): Promise<AgentStatus> => {
    const response = await apiClient.get('/agent/status');
    return response.data;
  },

  listConversations: async (params: { cursor?: string; q?: string } = {}): Promise<AgentConversationList> => {
    const response = await apiClient.get('/agent/conversations', { params });
    return response.data;
  },

  createConversation: async (): Promise<AgentConversation> => {
    const response = await apiClient.post('/agent/conversations', {});
    return response.data;
  },

  getConversation: async (id: string): Promise<AgentConversationDetail> => {
    const response = await apiClient.get(`/agent/conversations/${id}`);
    const data = response.data as {
      id: string;
      title: string | null;
      status: string;
      blocks: unknown[];
      pending_actions: unknown[];
    };
    return {
      id: data.id,
      title: data.title,
      status: data.status,
      // Same discipline as the stream: a block this build does not understand is
      // dropped, not rendered as a hole or thrown.
      blocks: data.blocks.map(parseAgentBlock).filter((block): block is AgentBlock => block !== null),
      // Same discipline as blocks: a pending action missing a required field or
      // carrying an unknown status is dropped, never handed to a confirmation
      // card typed as sound when nothing checked it.
      pending_actions: (data.pending_actions ?? [])
        .map(parseAgentAction)
        .filter((item): item is AgentPendingAction => item !== null),
    };
  },

  renameConversation: async (id: string, title: string): Promise<AgentConversation> => {
    const response = await apiClient.patch(`/agent/conversations/${id}`, { title });
    return response.data;
  },

  deleteConversation: async (id: string): Promise<void> => {
    await apiClient.delete(`/agent/conversations/${id}`);
  },

  sendMessage: (args: SendMessageArgs, options: AuthOptions & { signal?: AbortSignal } = {}) =>
    post(
      `/agent/conversations/${args.conversationId}/messages`,
      { text: args.text, page_context: args.page_context, timezone: args.timezone },
      options,
    ),

  applyAction: (actionId: string, context: ResolutionContext, options: AuthOptions & { signal?: AbortSignal } = {}) =>
    post(`/agent/actions/${actionId}/apply`, context, options),

  cancelAction: (actionId: string, context: ResolutionContext, options: AuthOptions & { signal?: AbortSignal } = {}) =>
    post(`/agent/actions/${actionId}/cancel`, context, options),

  applyAll: (conversationId: string, context: ResolutionContext, options: AuthOptions & { signal?: AbortSignal } = {}) =>
    post(`/agent/conversations/${conversationId}/actions/apply`, context, options),
};
